/**
 * Dossier Service
 * Save and recall analysis sessions using backend SQLite database
 * Persists across browser sessions and survives cache clears
 */

import apiClient from './apiClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? 'http://localhost:5001';

/**
 * Helper to get auth headers
 */
async function getAuthHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  try {
    const token = await apiClient.getAccessToken();
    headers['Authorization'] = `Bearer ${token}`;
  } catch (e) {
    console.warn('No auth token available for dossier request');
  }
  return headers;
}

/**
 * Get all saved dossiers from database
 */
export async function getAllDossiers() {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/analyses`, { headers });
    if (!response.ok) {
      throw new Error('Failed to fetch analyses');
    }
    const result = await response.json();

    // Transform to match expected format
    return (result.data || []).map(transformFromDb);
  } catch (error) {
    console.error('Error loading dossiers:', error);
    return [];
  }
}

/**
 * Save a new dossier to database
 * @param {Object} dossier - The dossier to save
 */
export async function saveDossier(dossier) {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/analyses`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        name: dossier.name,
        description: dossier.description || '',
        queries: dossier.queries || [],
        lastQuery: dossier.lastQuery || '',
        layer: dossier.layer || 'tracts',
        resultCount: dossier.resultCount || 0,
        analyticsData: dossier.analyticsData || null,
        tags: dossier.tags || [],
        county: dossier.county || '',
        operator: dossier.operator || ''
      })
    });

    if (!response.ok) {
      throw new Error('Failed to save analysis');
    }

    const result = await response.json();
    return transformFromDb(result.data);
  } catch (error) {
    console.error('Error saving dossier:', error);
    throw error;
  }
}

/**
 * Update an existing dossier
 */
export async function updateDossier(id, updates) {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/analyses/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      throw new Error('Failed to update analysis');
    }

    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error('Error updating dossier:', error);
    throw error;
  }
}

/**
 * Delete a dossier
 */
export async function deleteDossier(id) {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/analyses/${id}`, {
      method: 'DELETE',
      headers
    });

    if (!response.ok) {
      throw new Error('Failed to delete analysis');
    }

    return true;
  } catch (error) {
    console.error('Error deleting dossier:', error);
    throw error;
  }
}

/**
 * Get a specific dossier by ID
 */
export async function getDossierById(id) {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/analyses/${id}`, { headers });
    if (!response.ok) {
      return null;
    }
    const result = await response.json();
    return transformFromDb(result.data);
  } catch (error) {
    console.error('Error getting dossier:', error);
    return null;
  }
}

/**
 * Search dossiers by name or tags
 */
export async function searchDossiers(searchTerm) {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/analyses/search/${encodeURIComponent(searchTerm)}`, { headers });
    if (!response.ok) {
      return [];
    }
    const result = await response.json();
    return (result.data || []).map(transformFromDb);
  } catch (error) {
    console.error('Error searching dossiers:', error);
    return [];
  }
}

/**
 * Export a dossier to JSON file (client-side download)
 */
export async function exportDossier(id) {
  const dossier = await getDossierById(id);
  if (!dossier) {
    throw new Error('Dossier not found');
  }

  const blob = new Blob([JSON.stringify(dossier, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `tpl-analysis-${dossier.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${dossier.id}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Export all dossiers
 */
export async function exportAllDossiers() {
  const dossiers = await getAllDossiers();
  const blob = new Blob([JSON.stringify(dossiers, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `tpl-all-analyses-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Import a dossier from JSON
 */
export async function importDossier(jsonString) {
  try {
    const imported = JSON.parse(jsonString);

    // Handle single dossier or array
    const toImport = Array.isArray(imported) ? imported : [imported];
    let importedCount = 0;

    for (const dossier of toImport) {
      // Save each imported dossier to the database
      await saveDossier({
        name: dossier.name + ' (imported)',
        description: dossier.description || '',
        queries: dossier.queries || [],
        lastQuery: dossier.lastQuery || dossier.last_query || '',
        layer: dossier.layer || 'tracts',
        resultCount: dossier.resultCount || dossier.result_count || 0,
        analyticsData: dossier.analyticsData || dossier.analytics_data || null,
        tags: dossier.tags || [],
        county: dossier.county || '',
        operator: dossier.operator || ''
      });
      importedCount++;
    }

    return importedCount;
  } catch (error) {
    console.error('Error importing dossier:', error);
    throw new Error('Invalid analysis file format');
  }
}

/**
 * Get recent dossiers (last N)
 */
export async function getRecentDossiers(limit = 5) {
  const dossiers = await getAllDossiers();
  return dossiers.slice(0, limit);
}

/**
 * Transform database record to frontend format
 */
function transformFromDb(record) {
  if (!record) return null;

  return {
    id: record.id,
    name: record.name,
    description: record.description || '',
    createdAt: record.created_at || record.createdAt,
    updatedAt: record.updated_at || record.updatedAt,
    queries: record.queries || [],
    lastQuery: record.last_query || record.lastQuery || '',
    layer: record.layer || 'tracts',
    resultCount: record.result_count || record.resultCount || 0,
    analyticsData: record.analytics_data || record.analyticsData || null,
    tags: record.tags || [],
    county: record.county || '',
    operator: record.operator || ''
  };
}

export default {
  getAllDossiers,
  saveDossier,
  updateDossier,
  deleteDossier,
  getDossierById,
  searchDossiers,
  exportDossier,
  exportAllDossiers,
  importDossier,
  getRecentDossiers
};
